'use strict';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import { Brand } from '@/lenses/uilenses/theme/colors';
import dayjs from 'dayjs';
import { StudySummaryCard } from './StudySummaryCard.jsx';
import { StudyBuilderSectionTitle, studyBuilderGradientTopSx } from './StudyBuilderSectionTitle.jsx';
import { computeMockQuote, computeMockQuoteTierRange, formatCurrencyRangeUSD, formatCurrencyUSD } from '../studyBuilder.pricing.js';
import {
	COMMON_SPECIES_TILES,
	MATRIX_OPTIONS_BY_SPECIES,
	getSampleCountInputError,
	getTierSampleBounds,
	resolveStudySizeId,
} from '../studyBuilder.config.js';
import { useSendStudyBuilderQuoteSummaryEmailMutation } from '@/redux/services/portal/studyBuilder.api.js';
import { buildStudyBuilderQuoteSummaryPlainText } from '../studyBuilderQuoteSummaryPlainText.js';
import { COUNTRY_OPTIONS } from '../countryOptions.js';
import { formatDisplayDate } from '../studyBuilder.dateDisplay.js';
import {
	clearStudyBuilderGuestSession,
	mergeQuoteIntoGuestSession,
	readStudyBuilderGuestSession,
	writeStudyBuilderGuestSession,
} from '../studyBuilderGuestSession.js';
import { useAuth } from '@/hooks/auth.hook.js';

function initialLineRows(fit) {
	if (Array.isArray(fit.sampleLines) && fit.sampleLines.length) {
		const approximateScope = fit.matrixScopeMode === 'unsure' || fit.matrixScopeMode === 'multiple';
		return fit.sampleLines.map((ln, i) => ({
			id: ln.id || `ln-${i + 1}`,
			species: ln.species || '',
			matrix: ln.matrix || '',
			matrixCategory: ln.matrixCategory || '',
			matrixType: ln.matrixType || '',
			count: String(ln.count ?? ''),
			otherNotes: ln.species === 'Other' || approximateScope ? String(ln.otherNotes || '') : '',
		}));
	}
	return [
		{
			id: 'ln-1',
			species: '',
			matrix: '',
			matrixCategory: '',
			matrixType: '',
			count: '',
			otherNotes: '',
		},
	];
}

function defaultSampleInfo() {
	return {
		sampleVolume: '100μl',
		sampleShipping: 'Standard',
		plannedShippingDate: dayjs().add(14, 'day').format('YYYY-MM-DD'),
	};
}

function mergeSampleInfoFromDraft(draft) {
	const base = defaultSampleInfo();
	if (!draft?.sampleInfo || typeof draft.sampleInfo !== 'object') return base;
	return { ...base, ...draft.sampleInfo };
}

function mergeContactInfoFromDraft(draft) {
	const base = { firstName: '', lastName: '', email: '', company: '', country: '', message: '' };
	if (!draft?.contactInfo || typeof draft.contactInfo !== 'object') return base;
	return { ...base, ...draft.contactInfo };
}

function lineItemsFromQuoteDraft(draft, fitSelections) {
	if (!draft || !Array.isArray(draft.lineItems) || draft.lineItems.length === 0) return null;
	return draft.lineItems.map((ln, i) => ({
		id: ln.id || `ln-${i + 1}`,
		species: ln.species || '',
		matrix: ln.matrix || '',
		matrixCategory: ln.matrixCategory || '',
		matrixType: ln.matrixType || '',
		count: String(ln.count ?? ''),
		otherNotes: String(ln.otherNotes ?? ''),
	}));
}

/**
 * @param {{ fitSelections: Record<string, unknown>, primaryProduct: unknown, onBack: () => void, initialQuoteDraft?: Record<string, unknown>|null, enableGuestSessionPersistence?: boolean }} props
 */
export function QuoteWizard({
	fitSelections,
	primaryProduct,
	onBack,
	initialQuoteDraft = null,
	enableGuestSessionPersistence = false,
}) {
	const history = useHistory();
	const auth = useAuth();
	const isAuthed = auth.isAuthenticated();
	const [quoteRevealed, setQuoteRevealed] = useState(false);
	const [quoteSummaryEmailSent, setQuoteSummaryEmailSent] = useState(false);
	const [seekEstimateAttempted, setSeekEstimateAttempted] = useState(false);
	const speciesOptions = useMemo(() => [...COMMON_SPECIES_TILES], []);

	const [lineItems, setLineItems] = useState(() => lineItemsFromQuoteDraft(initialQuoteDraft, fitSelections) || initialLineRows(fitSelections));

	const [sampleInfo, setSampleInfo] = useState(() => mergeSampleInfoFromDraft(initialQuoteDraft));

	const [contactInfo, setContactInfo] = useState(() => mergeContactInfoFromDraft(initialQuoteDraft));
	const quoteDraftRef = useRef({ lineItems, sampleInfo, contactInfo });
	const quoteRevealedRef = useRef(quoteRevealed);
	quoteDraftRef.current = { lineItems, sampleInfo, contactInfo };

	useEffect(() => {
		quoteRevealedRef.current = quoteRevealed;
	}, [quoteRevealed]);

	const totalSamples = useMemo(
		() => lineItems.reduce((sum, row) => sum + (parseInt(row.count, 10) || 0), 0),
		[lineItems]
	);

	const quotePresentation = useMemo(() => {
		if (!primaryProduct?.id) return null;
		const studySizeId = resolveStudySizeId(fitSelections.studySize, totalSamples);
		const base = {
			productId: primaryProduct.id,
			studySizeId,
			sampleKit: 'no',
			plannedShippingDate: sampleInfo.plannedShippingDate,
			statsServices: fitSelections.statsServices,
			interpretationLevel: fitSelections.interpretationLevel,
		};
		const tierBounds = getTierSampleBounds(studySizeId);
		const useTierRange =
			Boolean(fitSelections.studySize)
			&& ((fitSelections.sampleCountMode || 'tier') === 'tier'
				|| (fitSelections.matrixScopeMode || 'single') !== 'single');
		if (useTierRange) {
			const r = computeMockQuoteTierRange(base);
			return {
				kind: 'range',
				estimatedCostDisplay: formatCurrencyRangeUSD(r.estimatedCostLow, r.estimatedCostHigh),
				estimatedCompletionDate: r.estimatedCompletionDate,
				sampleCountSummaryDisplay: `${tierBounds.lo}–${tierBounds.hi} (range)`,
				tierBounds,
			};
		}
		const n = Math.round(Number(fitSelections.exactSampleCount)) || totalSamples;
		const q = computeMockQuote({ ...base, numberOfSamples: n });
		return {
			kind: 'single',
			estimatedCostDisplay: formatCurrencyUSD(q.estimatedCost),
			estimatedCompletionDate: q.estimatedCompletionDate,
			sampleCountSummaryDisplay: `${n} (exact)`,
			tierBounds,
		};
	}, [
		primaryProduct,
		fitSelections.studySize,
		fitSelections.statsServices,
		fitSelections.interpretationLevel,
		fitSelections.sampleCountMode,
		fitSelections.exactSampleCount,
		fitSelections.matrixScopeMode,
		totalSamples,
		sampleInfo.plannedShippingDate,
	]);

	const estimatedCostDisplay = quotePresentation?.estimatedCostDisplay ?? null;
	const estimatedCompletionDisplay = quotePresentation?.estimatedCompletionDate
		? formatDisplayDate(quotePresentation.estimatedCompletionDate)
		: null;
	const quoteSampleBasisDisplay = quotePresentation?.kind === 'range' ? quotePresentation.sampleCountSummaryDisplay : null;

	const contactOk =
		contactInfo.firstName.trim()
		&& contactInfo.lastName.trim()
		&& contactInfo.email.includes('@')
		&& contactInfo.company.trim()
		&& COUNTRY_OPTIONS.includes(contactInfo.country);
	const countryMissing = seekEstimateAttempted && !COUNTRY_OPTIONS.includes(contactInfo.country);
	const canSeeEstimate = contactOk && sampleInfo.sampleVolume && sampleInfo.sampleShipping && sampleInfo.plannedShippingDate;

	const seekEstimateValidationMessage = useMemo(() => {
		if (!seekEstimateAttempted || canSeeEstimate) return null;

		const missingContact = [
			!contactInfo.firstName.trim() && 'first name',
			!contactInfo.lastName.trim() && 'last name',
			!contactInfo.email.includes('@') && 'email',
			!contactInfo.company.trim() && 'company name',
			!COUNTRY_OPTIONS.includes(contactInfo.country) && 'country',
		].filter(Boolean);

		if (missingContact.length > 0) {
			return 'Complete all required contact fields (first name, last name, email, company, and country) to see your estimate.';
		}

		const missingOther = [
			!sampleInfo.sampleVolume && 'sample volume',
			!sampleInfo.sampleShipping && 'sample shipping',
			!sampleInfo.plannedShippingDate && 'planned shipping date',
		].filter(Boolean);

		if (missingOther.length === 1) {
			return `Complete ${missingOther[0]} to see your estimate.`;
		}
		if (missingOther.length > 1) {
			const last = missingOther.pop();
			return `Complete ${missingOther.join(', ')} and ${last} to see your estimate.`;
		}

		return 'Complete the required fields to see your estimate.';
	}, [
		seekEstimateAttempted,
		canSeeEstimate,
		contactInfo.firstName,
		contactInfo.lastName,
		contactInfo.email,
		contactInfo.company,
		contactInfo.country,
		sampleInfo.sampleVolume,
		sampleInfo.sampleShipping,
		sampleInfo.plannedShippingDate,
	]);

	useEffect(() => {
		if (canSeeEstimate) setSeekEstimateAttempted(false);
	}, [canSeeEstimate]);

	const [sendQuoteSummaryEmail, { isLoading: quoteSummaryEmailSending, isError: quoteSummaryEmailError, reset: resetQuoteEmailMutation }] =
		useSendStudyBuilderQuoteSummaryEmailMutation();
	const prevQuoteRevealedRef = useRef(false);

	const quoteDraftForEmail = useMemo(
		() => ({
			lineItems,
			sampleInfo: { ...sampleInfo, sampleKit: 'no' },
			contactInfo,
		}),
		[lineItems, sampleInfo, contactInfo]
	);

	const buildQuoteEmailPayload = useCallback(() => {
		const plainTextBody = buildStudyBuilderQuoteSummaryPlainText({
			fitSelections,
			primaryProduct,
			draft: quoteDraftForEmail,
			totalSamples,
			estimatedCostDisplay,
			estimatedCompletionDisplay,
			quoteSampleBasisDisplay,
		});
		return {
			toEmail: contactInfo.email.trim(),
			contact: {
				firstName: contactInfo.firstName,
				lastName: contactInfo.lastName,
				email: contactInfo.email,
				company: contactInfo.company,
				country: contactInfo.country,
				message: contactInfo.message,
			},
			plainTextBody,
		};
	}, [
		fitSelections,
		primaryProduct,
		quoteDraftForEmail,
		totalSamples,
		estimatedCostDisplay,
		estimatedCompletionDisplay,
		quoteSampleBasisDisplay,
		contactInfo.email,
		contactInfo.firstName,
		contactInfo.lastName,
		contactInfo.company,
		contactInfo.country,
		contactInfo.message,
	]);

	const handleRetryQuoteEmail = useCallback(() => {
		resetQuoteEmailMutation();
		sendQuoteSummaryEmail(buildQuoteEmailPayload())
			.unwrap()
			.then(() => setQuoteSummaryEmailSent(true))
			.catch(() => {});
	}, [buildQuoteEmailPayload, resetQuoteEmailMutation, sendQuoteSummaryEmail]);

	useEffect(() => {
		if (!quoteRevealed) return;
		clearStudyBuilderGuestSession();
	}, [quoteRevealed]);

	useEffect(() => {
		if (!isAuthed || quoteRevealed) return;
		const g = readStudyBuilderGuestSession();
		if (!g?.revealEstimateAfterLogin) return;
		writeStudyBuilderGuestSession({ revealEstimateAfterLogin: false });
		setQuoteRevealed(true);
	}, [isAuthed, quoteRevealed]);

	useEffect(() => {
		if (quoteRevealed) return;
		const id = window.setTimeout(() => {
			mergeQuoteIntoGuestSession(quoteDraftRef.current);
			if (enableGuestSessionPersistence) {
				writeStudyBuilderGuestSession({
					selections: fitSelections,
					phase: 'build',
				});
			}
		}, 400);
		return () => {
			window.clearTimeout(id);
			if (!quoteRevealedRef.current) {
				mergeQuoteIntoGuestSession(quoteDraftRef.current);
			}
		};
	}, [
		enableGuestSessionPersistence,
		quoteRevealed,
		fitSelections,
		lineItems,
		sampleInfo,
		contactInfo,
	]);

	useEffect(() => {
		if (!quoteRevealed) {
			prevQuoteRevealedRef.current = false;
			return;
		}
		const firstReveal = !prevQuoteRevealedRef.current;
		prevQuoteRevealedRef.current = true;
		if (!firstReveal || quoteSummaryEmailSent) return;

		sendQuoteSummaryEmail(buildQuoteEmailPayload())
			.unwrap()
			.then(() => setQuoteSummaryEmailSent(true))
			.catch(() => {});
	}, [quoteRevealed, quoteSummaryEmailSent, sendQuoteSummaryEmail, buildQuoteEmailPayload]);

	const persistGuestSessionAndRedirectToLogin = useCallback(() => {
		writeStudyBuilderGuestSession({
			selections: fitSelections,
			phase: 'build',
			revealEstimateAfterLogin: true,
			quote: {
				lineItems,
				sampleInfo,
				contactInfo,
			},
		});
		const returnPath = `${window.location.origin}/home/build-a-study`;
		localStorage.setItem('returnUrl', returnPath);
		history.push(`/login?return_url=${encodeURIComponent(returnPath)}`);
	}, [contactInfo, fitSelections, history, lineItems, sampleInfo]);

	const handleSeekEstimateClick = useCallback(() => {
		if (!canSeeEstimate) {
			setSeekEstimateAttempted(true);
			return;
		}
		if (!auth.isAuthenticated()) {
			persistGuestSessionAndRedirectToLogin();
			return;
		}
		setQuoteRevealed(true);
	}, [auth, canSeeEstimate, persistGuestSessionAndRedirectToLogin]);

	const updateLine = (id, patch) => {
		setLineItems((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
	};

	const addLine = () => {
		setLineItems((rows) => [...rows, { id: `ln-${Date.now()}`, species: '', matrix: '', matrixCategory: '', matrixType: '', count: '', otherNotes: '' }]);
	};

	const removeLine = (id) => {
		setLineItems((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.id !== id)));
	};

	if (quoteRevealed) {
		return (
			<Box data-testid="quote-wizard">
				<Grid container spacing={3} alignItems="flex-start">
					<Grid item xs={12} md={7}>
						<Paper
							elevation={0}
							sx={{
								p: { xs: 3, md: 5 },
								borderRadius: 3,
								border: `1px solid ${Brand.precisionGrey}`,
								overflow: 'hidden',
								textAlign: 'center',
								'&::before': studyBuilderGradientTopSx,
							}}
						>
							<Box sx={{ pt: 2 }}>
								<CheckCircleOutline sx={{ fontSize: 56, color: Brand.livingPurple, mb: 1.5 }} />
								<Typography variant="h4" sx={{ fontWeight: 800, color: Brand.darkModeBlue, mb: 1 }}>
									Here is your indicative estimate
								</Typography>
								<Typography variant="body1" sx={{ color: Brand.molecularBlue, mb: 2, lineHeight: 1.75, maxWidth: 560, mx: 'auto' }}>
									Thank you, <strong>{contactInfo.firstName}</strong>. Someone from our team will be in touch to confirm scope, timelines,
									and final pricing.
								</Typography>

								{quoteSummaryEmailSending && !quoteSummaryEmailSent ? (
									<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
										<CircularProgress size={22} sx={{ color: Brand.livingPurple }} />
										<Typography variant="body2" sx={{ color: Brand.molecularBlue }}>
											Sending a copy of your summary and estimate to <strong>{contactInfo.email}</strong>…
										</Typography>
									</Box>
								) : null}
								{quoteSummaryEmailSent ? (
									<Typography variant="body2" sx={{ color: Brand.darkModeBlue, mb: 2, maxWidth: 560, mx: 'auto' }}>
										We emailed a copy of this summary and indicative estimate to <strong>{contactInfo.email}</strong>.
									</Typography>
								) : null}
								{quoteSummaryEmailError && !quoteSummaryEmailSent ? (
									<Alert
										severity="warning"
										sx={{ mb: 2, textAlign: 'left', maxWidth: 560, mx: 'auto' }}
										action={
											<Button color="inherit" size="small" onClick={handleRetryQuoteEmail} sx={{ fontWeight: 700 }}>
												Retry
											</Button>
										}
									>
										We could not email your summary just now. You can try again, or save this page for your records.
									</Alert>
								) : null}

								<Box
									sx={{
										borderRadius: 3,
										p: { xs: 2.5, md: 3.5 },
										mb: 3,
										background: `linear-gradient(135deg, rgba(40, 74, 111, 0.12) 0%, rgba(133, 93, 163, 0.1) 50%, rgba(255, 79, 88, 0.08) 100%)`,
										border: `1px solid ${Brand.precisionGrey}`,
									}}
								>
									<Typography variant="overline" sx={{ color: Brand.molecularBlue, fontWeight: 700, letterSpacing: 2 }}>
										Estimated study investment
									</Typography>
									<Typography
										data-testid="quote-confirmed-estimated-cost"
										variant="h3"
										sx={{ fontWeight: 800, color: Brand.darkModeBlue, my: 1.5, letterSpacing: '-0.03em' }}
									>
										{estimatedCostDisplay}
									</Typography>
									<Typography variant="body2" sx={{ color: Brand.molecularBlue }}>
										{quotePresentation?.kind === 'range' && quotePresentation.tierBounds ? (
											<>
												Based on the <strong>
													{quotePresentation.tierBounds.lo}–{quotePresentation.tierBounds.hi}
												</strong>{' '}
												sample range for your study size (indicative) · target completion around{' '}
												<strong>{estimatedCompletionDisplay}</strong>
											</>
										) : (
											<>
												Based on <strong>{totalSamples}</strong> total samples across your matrix lines · target completion is around{' '}
												<strong>{estimatedCompletionDisplay}</strong>
											</>
										)}
									</Typography>
								</Box>

								<Button variant="outlined" onClick={onBack} sx={{ borderColor: Brand.molecularBlue, color: Brand.molecularBlue, fontWeight: 600 }}>
									Back to selections
								</Button>
							</Box>
						</Paper>
					</Grid>
					<Grid item xs={12} md={5}>
						<StudySummaryCard
							fitSelections={fitSelections}
							primaryProduct={primaryProduct}
							sampleInfo={{
								...sampleInfo,
								numberOfSamples: String(totalSamples),
							}}
							totalSampleCount={totalSamples}
							quoteSampleBasisDisplay={quoteSampleBasisDisplay}
							estimatedCostDisplay={estimatedCostDisplay}
							estimatedCompletionDisplay={estimatedCompletionDisplay}
							showEstimatedCost
						/>
					</Grid>
				</Grid>
			</Box>
		);
	}

	return (
		<Box data-testid="quote-wizard">
			<Paper
				elevation={0}
				sx={{
					mb: 3,
					borderRadius: 3,
					border: `1px solid ${Brand.precisionGrey}`,
					background: Brand.pureWhite,
					boxShadow: '0 12px 40px rgba(0, 29, 56, 0.06)',
					overflow: 'hidden',
					'&::before': studyBuilderGradientTopSx,
				}}
			>
				<Box sx={{ p: { xs: 2.5, md: 4 }, pt: { xs: 2, md: 3 } }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 1 }}>
						<Box>
							<Typography
								variant="h4"
								component="h2"
								sx={{
									fontWeight: 800,
									color: Brand.darkModeBlue,
									fontSize: { xs: '1.35rem', sm: '1.65rem' },
									letterSpacing: '-0.02em',
								}}
							>
								Study details & quote
							</Typography>
						</Box>
					</Box>
				</Box>
			</Paper>

			<Grid container spacing={3}>
				<Grid item xs={12} md={7}>
					<Paper
						elevation={0}
						sx={{
							p: { xs: 2.5, md: 3 },
							borderRadius: 3,
							border: `1px solid ${Brand.precisionGrey}`,
							background: `linear-gradient(180deg, ${Brand.pureWhite} 0%, rgba(242, 250, 252, 0.5) 100%)`,
							boxShadow: '0 4px 24px rgba(0, 29, 56, 0.04)',
						}}
					>
						<Box data-testid="wizard-step-sample-info">
							<Paper variant="outlined" sx={{ p: 1.5, mb: 3, borderColor: Brand.molecularBlue, background: 'rgba(40,74,111,0.04)' }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 800, color: Brand.darkModeBlue }}>
									Total samples: {totalSamples}
								</Typography>
							</Paper>

							<StudyBuilderSectionTitle title="Shipping & handling" />
							<TextField
								fullWidth
								select
								label="Sample volume"
								value={sampleInfo.sampleVolume}
								onChange={(e) => setSampleInfo((s) => ({ ...s, sampleVolume: e.target.value }))}
								margin="normal"
								inputProps={{ 'data-testid': 'sample-volume-select' }}
							>
								{['50μl', '100μl', '200μl', '500μl', '1ml'].map((v) => (
									<MenuItem key={v} value={v}>
										{v}
									</MenuItem>
								))}
							</TextField>
							<TextField
								fullWidth
								select
								label="Sample shipping"
								value={sampleInfo.sampleShipping}
								onChange={(e) => setSampleInfo((s) => ({ ...s, sampleShipping: e.target.value }))}
								margin="normal"
							>
								{['Standard', 'Expedited', 'Customer arranged'].map((v) => (
									<MenuItem key={v} value={v}>
										{v}
									</MenuItem>
								))}
							</TextField>
							<TextField
								fullWidth
								type="date"
								label="Planned shipping date"
								InputLabelProps={{ shrink: true }}
								value={sampleInfo.plannedShippingDate}
								onChange={(e) => setSampleInfo((s) => ({ ...s, plannedShippingDate: e.target.value }))}
								margin="normal"
								inputProps={{ 'data-testid': 'planned-shipping-date' }}
							/>

						<StudyBuilderSectionTitle title="Contact" />
						<TextField
							fullWidth
							required
							label="First Name"
							value={contactInfo.firstName}
							onChange={(e) => setContactInfo((s) => ({ ...s, firstName: e.target.value }))}
							margin="normal"
							inputProps={{ 'data-testid': 'contact-name' }}
						/>
						<TextField
							fullWidth
							required
							label="Last Name"
							value={contactInfo.lastName}
							onChange={(e) => setContactInfo((s) => ({ ...s, lastName: e.target.value }))}
							margin="normal"
							inputProps={{ 'data-testid': 'contact-lastname' }}
						/>
						<TextField
							fullWidth
							required
							type="email"
							label="Email"
							value={contactInfo.email}
							onChange={(e) => setContactInfo((s) => ({ ...s, email: e.target.value }))}
							margin="normal"
							inputProps={{ 'data-testid': 'contact-email' }}
						/>
						<TextField
							fullWidth
							required
							label="Company Name"
							value={contactInfo.company}
							onChange={(e) => setContactInfo((s) => ({ ...s, company: e.target.value }))}
							margin="normal"
							inputProps={{ 'data-testid': 'contact-org' }}
						/>
						<TextField
							fullWidth
							required
							select
							value={contactInfo.country}
							onChange={(e) => setContactInfo((s) => ({ ...s, country: e.target.value }))}
							margin="normal"
							error={countryMissing}
							helperText={countryMissing ? 'Select a country' : undefined}
							inputProps={{ 'data-testid': 'contact-country', 'aria-label': 'Country' }}
							SelectProps={{
								displayEmpty: true,
								MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
							}}
						>
							<MenuItem value="">
								<em>Select a country</em>
							</MenuItem>
							{COUNTRY_OPTIONS.map((country) => (
								<MenuItem key={country} value={country}>
									{country}
								</MenuItem>
							))}
						</TextField>
						<TextField
							fullWidth
							multiline
							minRows={3}
							label="Message"
							placeholder="Anything else you'd like us to know?"
							value={contactInfo.message}
							onChange={(e) => setContactInfo((s) => ({ ...s, message: e.target.value }))}
							margin="normal"
							inputProps={{ 'data-testid': 'contact-message' }}
						/>
						</Box>

						{seekEstimateValidationMessage ? (
							<Alert severity="warning" sx={{ mt: 2, mb: 1 }} data-testid="quote-seek-validation-hint">
								{seekEstimateValidationMessage}
							</Alert>
						) : null}

						<Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
							<Button
								variant="outlined"
								onClick={onBack}
								sx={{
									borderColor: Brand.precisionGrey,
									color: Brand.molecularBlue,
									fontWeight: 600,
									borderRadius: 2,
									'&:hover': { borderColor: Brand.molecularBlue, background: 'rgba(40, 74, 111, 0.06)' },
								}}
							>
								Back to selections
							</Button>
							<Button
								data-testid="get-your-quote"
								variant="contained"
								onClick={handleSeekEstimateClick}
								sx={{
									background: Brand.activatedRed,
									fontWeight: 700,
									borderRadius: 2,
									boxShadow: 'none',
									'&:hover': { background: Brand.insightfulOrange },
									...(!canSeeEstimate && { opacity: 0.55 }),
								}}
							>
								See your estimate
							</Button>
						</Box>
					</Paper>
				</Grid>

				<Grid item xs={12} md={5}>
					<StudySummaryCard
						fitSelections={fitSelections}
						primaryProduct={primaryProduct}
						sampleInfo={{
							...sampleInfo,
							numberOfSamples: String(totalSamples),
						}}
						totalSampleCount={totalSamples}
						quoteSampleBasisDisplay={quoteSampleBasisDisplay}
						estimatedCostDisplay={estimatedCostDisplay}
						estimatedCompletionDisplay={estimatedCompletionDisplay}
						showEstimatedCost={false}
					/>
				</Grid>
			</Grid>
		</Box>
	);
}
